import Papa, {
    type LocalFile,
    type ParseConfig,
    type ParseResult,
    type ParseMeta,
} from 'papaparse';
import { removeColumns, findEmptyColumns, EMPTY_VALUE } from './helpers';
import type { ExtendedParseResult } from './types';

interface ExtendedParseMeta extends ParseMeta {
    renamedHeaders?: Record<string, string>;
}

export const parseCSVFile = <T extends Record<string, unknown>>(
    file: File,
    complete: (results: ExtendedParseResult<T>) => void,
    error: (err: Error) => void,
    config?: Partial<ParseConfig<T, LocalFile>>,
) => {
    const handleComplete = (results: ParseResult<T>) => {
        const extendedMeta = results.meta as ExtendedParseMeta;
        const renamedHeaders = extendedMeta.renamedHeaders || {};

        const columnsToRemove = [
            ...Object.entries(renamedHeaders)
                .filter(([_, originalHeader]) => !originalHeader.trim())
                .map(([renamedHeader]) => renamedHeader),
            ...(extendedMeta.fields || []).filter((field) => !field.trim()),
        ];

        let processedData = removeColumns(results.data, columnsToRemove);
        const emptyColumnsWithHeaders = findEmptyColumns(processedData, columnsToRemove);

        const updatedFields = (extendedMeta.fields || []).filter(
            (field) => field.trim() && !columnsToRemove.includes(field),
        );

        processedData = processedData.map((row) => {
            const cleanRow: Record<string, unknown> = { ...row };
            Object.entries(cleanRow).forEach(([key, value]) => {
                if (value === EMPTY_VALUE) {
                    cleanRow[key] = null;
                }
            });
            return cleanRow as T;
        });

        complete({
            data: processedData,
            errors: results.errors,
            meta: {
                ...extendedMeta,
                fields: updatedFields,
                emptyColumns: emptyColumnsWithHeaders,
            },
        });
    };

    Papa.parse<T>(file, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        complete: handleComplete,
        error,
        ...config,
    });
};
